'use client'

import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  defaultDict,
  defaultLocale,
  getByPath,
  loadDict,
  locales,
  type Dict,
  type Locale,
} from '@/lib/i18n'

type I18nContextType = {
  locale: Locale
  dict: Dict
  t: (path: string) => string
  setLocale: (next: Locale) => void
  locales: readonly Locale[]
}

const I18nContext = createContext<I18nContextType | null>(null)

export function I18nProvider({
  children,
  initialLocale,
}: {
  children: React.ReactNode
  initialLocale?: Locale
}) {
  // locale 始终对应已加载的 dict；initialLocale 非默认语言时先渲染默认语言，字典就绪后切换
  const [locale, setLocaleState] = useState<Locale>(defaultLocale)
  const [dict, setDict] = useState<Dict>(defaultDict)
  const pendingLocale = useRef<Locale>(initialLocale ?? defaultLocale)

  // 加载并切换到目标语言；异步加载期间保持旧语言，避免 key 闪烁
  const applyLocale = (next: Locale) => {
    pendingLocale.current = next
    if (next === defaultLocale) {
      setDict(defaultDict)
      setLocaleState(next)
      return
    }
    loadDict(next).then((loaded) => {
      if (pendingLocale.current === next) {
        setDict(loaded)
        setLocaleState(next)
      }
    })
  }

  // 首次挂载时尝试从 localStorage 恢复
  useEffect(() => {
    try {
      const stored = (localStorage.getItem('locale') || '') as Locale
      if (stored && (locales as readonly string[]).includes(stored)) {
        applyLocale(stored as Locale)
      } else if ((initialLocale ?? defaultLocale) !== defaultLocale) {
        applyLocale(initialLocale ?? defaultLocale)
      }
    } catch (e) {
      if (process.env.NODE_ENV === 'development')
        console.debug('[i18n] 读取本地语言失败', (e as Error)?.message)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setLocale = (next: Locale) => {
    applyLocale(next)
    try {
      localStorage.setItem('locale', next)
    } catch (e) {
      if (process.env.NODE_ENV === 'development')
        console.debug('[i18n] 保存本地语言失败', (e as Error)?.message)
    }
    try {
      document.cookie = `locale=${next}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax`
    } catch (e) {
      if (process.env.NODE_ENV === 'development')
        console.debug('[i18n] 写入cookie失败', (e as Error)?.message)
    }
  }

  const t = useMemo(() => {
    return (path: string) => {
      const value = getByPath(dict, path)
      if (value === undefined) {
        if (process.env.NODE_ENV === 'development') {
          // 开发期告警：缺失的字典键
          console.warn(`[i18n] Missing key: ${path} (locale: ${locale})`)
        }
        return path
      }
      return value
    }
  }, [dict, locale])

  const value = useMemo(() => ({ locale, dict, t, setLocale, locales }), [locale, dict, t])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n() {
  const ctx = useContext(I18nContext)
  if (!ctx) throw new Error('useI18n must be used within I18nProvider')
  return ctx
}

// 语言切换器：地球图标 + 下拉菜单样式
export function LanguageSwitcher({ className }: { className?: string }) {
  const { locale, setLocale } = useI18n()
  const [isOpen, setIsOpen] = useState(false)

  const languages = [
    { value: 'en', label: 'English' },
    { value: 'zh-CN', label: '简体中文' },
    { value: 'zh-TW', label: '繁體中文' },
  ]

  const handleSelect = (value: Locale) => {
    setLocale(value)
    setIsOpen(false)
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-center w-10 h-10 rounded-full hover:bg-accent transition-colors ${className || ''}`}
        aria-label="切换语言"
      >
        <svg
          className="w-5 h-5 text-foreground"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </button>

      {isOpen && (
        <>
          {/* 点击遮罩关闭 */}
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />

          {/* 语言选择菜单 */}
          <div className="absolute top-full right-0 mt-2 w-40 bg-background border border-input rounded-lg shadow-lg z-50 overflow-hidden">
            {languages.map((lang) => (
              <button
                key={lang.value}
                onClick={() => handleSelect(lang.value as Locale)}
                className={`w-full px-4 py-3 text-left hover:bg-accent transition-colors flex items-center gap-3 ${
                  locale === lang.value ? 'font-medium' : ''
                }`}
              >
                {locale === lang.value && (
                  <div className="w-2 h-2 rounded-full bg-foreground flex-shrink-0" />
                )}
                {locale !== lang.value && <div className="w-2 h-2 flex-shrink-0" />}
                <span className="text-sm">{lang.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
